/**
 * LiquidFilters — global SVG filter definitions used across the app.
 *
 * Rendered once at the App root in a 0×0 SVG so its <defs> are
 * referenceable from any element via `filter: url(#id)` (or — for the
 * Liquid Glass warp — via the backdrop-filter list).
 *
 * Filters defined:
 *
 *   - lg-glass-warp: feTurbulence + feDisplacementMap tuned for use
 *     INSIDE the backdrop-filter list. This is what gives the glass
 *     its physical "lens" refraction — pixels behind the surface get
 *     subtly warped along an organic noise field, exactly like real
 *     curved glass over a colour mesh. Used by `.lg-pill-blur` and
 *     `.lg-surface-blur` via a stacked backdrop-filter declaration.
 *
 *   - liquid-distort: legacy filter kept for component API parity.
 *
 *   - liquid-edge: soft alpha-channel blur for organic glass borders.
 *
 *   - liquid-chroma: chromatic aberration accent for hover states.
 *
 * NOTE on browser support: backdrop-filter with a url() filter
 * function works in Safari 17+ and Chromium 116+. Telegram WebView on
 * iOS uses the system WebKit (>= iOS 17 in 2026), and Android uses
 * Chrome WebView (>= 120). Both render the displacement correctly.
 * Older clients gracefully fall back to the plain blur+saturate.
 */
export function LiquidFilters() {
  return (
    <svg
      aria-hidden
      width="0"
      height="0"
      style={{ position: 'absolute', pointerEvents: 'none' }}
    >
      <defs>
        {/* Liquid Glass refraction — applied via filter:url() on a
            DEDICATED empty layer that sits between the backdrop and
            the nav content. Because the layer is empty, the
            displacement only warps the rendered backdrop result, not
            the icons or text.

            scale 28 = strong, visible refraction (matches iPhone
            Liquid Glass capsules in the Telegram dock screenshot).
            baseFrequency 0.006 0.010 = wide, slow noise waves so
            the warp looks like a smooth lens, not jittery static.
            seed 5 = fixed pattern, no frame-to-frame jitter. */}
        <filter
          id="lg-glass-warp"
          x="-10%"
          y="-10%"
          width="120%"
          height="120%"
          colorInterpolationFilters="sRGB"
        >
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.006 0.010"
            numOctaves="2"
            seed="5"
            result="warp-noise"
          />
          <feDisplacementMap
            in="SourceGraphic"
            in2="warp-noise"
            scale="28"
            xChannelSelector="R"
            yChannelSelector="G"
          />
        </filter>

        {/* Subtle liquid refraction — turbulence noise + displacement */}
        <filter
          id="liquid-distort"
          x="-20%"
          y="-20%"
          width="140%"
          height="140%"
          colorInterpolationFilters="sRGB"
        >
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.012 0.018"
            numOctaves="2"
            seed="7"
            result="noise"
          />
          <feDisplacementMap
            in="SourceGraphic"
            in2="noise"
            scale="6"
            xChannelSelector="R"
            yChannelSelector="G"
          />
        </filter>

        {/* Soft edge feathering for organic glass borders */}
        <filter id="liquid-edge" x="-10%" y="-10%" width="120%" height="120%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="1.2" result="blurred" />
          <feMerge>
            <feMergeNode in="blurred" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Chromatic aberration accent for hover states */}
        <filter id="liquid-chroma" x="-10%" y="-10%" width="120%" height="120%">
          <feColorMatrix
            type="matrix"
            values="1 0 0 0 0.02
                    0 1 0 0 0
                    0 0 1 0 -0.02
                    0 0 0 1 0"
          />
        </filter>
      </defs>
    </svg>
  );
}
